import { useCallback } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useQaWebSocket } from "@/hooks/use-websocket";
import { useLiveRefresh } from "@/lib/live-refresh";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Radio, AlertTriangle, ShieldCheck, Image as ImageIcon } from "lucide-react";
import type { ActiveTaskRow } from "@shared/active-task";
import { sortActiveRows, earlyRiskBadgeLabel, isActiveListEmpty, shouldRefetchActiveList, formatDeliveryWindow, formatEst } from "@/lib/active-helpers";

// SPEC-ActiveTask Phase A — Surface 1. Live list of all active (onfleetState=2)
// tasks so driver support can jump to the QAM reference for an in-progress
// delivery. No date picker (live set). At-risk rows float to the top, then
// alphabetical by driver (sort locked 2026-05-22). Teal identity colour.
const TEAL = "#0E7490";

const ACTIVE_LIST_KEY = ["/api/qa/active/list"];

export default function ActiveTaskList() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery<{ tasks: ActiveTaskRow[] }>({
    queryKey: ACTIVE_LIST_KEY,
    queryFn: async () => {
      const res = await fetch("/api/qa/active/list");
      if (!res.ok) throw new Error("Failed to fetch active tasks");
      return res.json();
    },
  });

  // Live add/remove: any active_* / completion event re-pulls the live set.
  // Reuses the QA-list websocket pattern (the data is small, so a refetch on
  // event is simpler and race-free vs. patching the array in place).
  // A12 (2026-09-24 validation): same hidden-tab rule as the senior list —
  // at most one reload per 5 s, none while the tab is hidden, one when shown.
  const refreshActiveList = useLiveRefresh(() => {
    queryClient.invalidateQueries({ queryKey: ACTIVE_LIST_KEY });
  });
  const handleQaUpdate = useCallback((event: { type: string }) => {
    if (shouldRefetchActiveList(event.type)) {
      refreshActiveList();
    }
  }, [refreshActiveList]);

  useQaWebSocket(user?.id ?? null, handleQaUpdate, {
    userName: (user as any)?.firstName || undefined,
    email: user?.email || undefined,
  });

  const rows = sortActiveRows((data?.tasks ?? []) as ActiveTaskRow[]);

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <Radio className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-lg font-semibold" style={{ color: TEAL }}>Active Tasks</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Drivers currently on a delivery. Open one for the QAM reference (verified photos, notes, expected predictions).
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading active tasks…
        </div>
      ) : isActiveListEmpty(rows) ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground" data-testid="active-empty-state">
            No active tasks right now.
          </CardContent>
        </Card>
      ) : (
        <div style={{ borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left">
                <th className="px-3 py-2 font-medium">Driver</th>
                <th className="px-3 py-2 font-medium">Recipient</th>
                <th className="px-3 py-2 font-medium">Destination</th>
                <th className="px-3 py-2 font-medium">Delivery Window (EST)</th>
                <th className="px-3 py-2 font-medium">Est. Completion (EST)</th>
                <th className="px-3 py-2 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const badge = earlyRiskBadgeLabel(row.earlyRisk);
                return (
                  <tr
                    key={row.id}
                    className="border-t hover:bg-muted/40 cursor-pointer"
                    data-testid={`active-row-${row.id}`}
                    style={badge ? { background: "var(--badBg, #fef2f2)" } : undefined}
                  >
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block">
                        <div className="font-medium">{row.worker.name ?? "—"}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {row.isQatt1 && (
                            <Badge variant="outline" className="text-[10px]" style={{ borderColor: TEAL, color: TEAL }}>CookUnity</Badge>
                          )}
                          {row.prescreened && (
                            <Badge variant="outline" className="text-[10px] gap-0.5"><ShieldCheck className="h-3 w-3" />Prescreened</Badge>
                          )}
                          {row.hasPdr && (
                            <Badge variant="outline" className="text-[10px] gap-0.5"><ImageIcon className="h-3 w-3" />Reference</Badge>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block">{row.recipient ?? "—"}</Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block">{row.destination.full ?? "—"}</Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block">
                        {formatDeliveryWindow(row.completeAfter, row.completeBefore)}
                        {badge && (
                          <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--bad, #dc2626)" }} data-testid={`active-early-badge-${row.id}`}>
                            <AlertTriangle className="h-3 w-3" />{badge}
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block" data-testid={`active-ect-${row.id}`}>{formatEst(row.ect)}</Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link href={`/qa/active/${row.id}`} className="block">{row.flagCount > 0 ? row.flagCount : "—"}</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
